import { md5 } from "js-md5";
import moment from "moment";
import posthog from "posthog-js";

import { POSTHOG_ACTIVE } from "../app.config";
import i18n from "../i18n";
import { queryClient } from "../lib/queryClient";
import { BookingFetchResponseType } from "../models/services/booking";
import { locationType } from "../models/services/organisation";
import { userProfile } from "../models/services/user";
import {
  CancellationMetricsResult,
  ParticipantCountOptions,
  TrackAvailabilityViewedProperties,
  TrackBookingCancelledProperties,
  TrackBookingConfirmedProperties,
  TrackBookingDetailsViewedProperties,
  TrackBookingEditedProperties,
  TrackBookingEditStartProperties,
  TrackBookingEditSubmittedProperties,
  TrackChargeFailedProperties,
  TrackChargeRefundedProperties,
  TrackChargeSucceededProperties,
  TrackLocationListViewedProperties,
  TrackLoginSucceededOptions,
  TrackPaymentStepReachedProperties,
  TrackRaiseNowChargeResult,
  TrackSlotSelectedProperties,
} from "../models/utils/analytics.model";
import { applyLocalization } from "../services/common.service";
import { getUserProfile } from "../services/user.service";

export * from "../models/utils/analytics.model";

// Helper to generate UUID
export const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// Safe Storage helpers to prevent crashes when storage is blocked (e.g. SecurityError in private browsing or embedded iframes)
export const analyticsWebSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.sessionStorage !== "undefined"
      ) {
        return window.sessionStorage.getItem(key);
      }
    } catch {
      // Non-blocking catch for SecurityError / storage blocked
    }
    return null;
  },
  removeItem: (key: string): void => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.sessionStorage !== "undefined"
      ) {
        window.sessionStorage.removeItem(key);
      }
    } catch {
      // Non-blocking catch for SecurityError
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.sessionStorage !== "undefined"
      ) {
        window.sessionStorage.setItem(key, value);
      }
    } catch {
      // Non-blocking catch for quota or SecurityError
    }
  },
};

export const analyticsLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.localStorage !== "undefined"
      ) {
        return window.localStorage.getItem(key);
      }
    } catch {
      // Non-blocking catch for SecurityError / storage blocked
    }
    return null;
  },
  removeItem: (key: string): void => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.localStorage !== "undefined"
      ) {
        window.localStorage.removeItem(key);
      }
    } catch {
      // Non-blocking catch for SecurityError
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.localStorage !== "undefined"
      ) {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // Non-blocking catch for quota or SecurityError
    }
  },
};

// In-memory fallback if storage is inaccessible or blocked
let inMemorySessionId: string | null = null;

// Mint session_id lazily on first access to avoid SecurityError at module import time
export const getAnalyticsSessionId = (): string => {
  if (inMemorySessionId) {
    return inMemorySessionId;
  }
  const stored = analyticsWebSessionStorage.getItem("availio_session_id");
  if (stored) {
    inMemorySessionId = stored;
    return stored;
  }
  const newId = generateUUID();
  inMemorySessionId = newId;
  analyticsWebSessionStorage.setItem("availio_session_id", newId);
  return newId;
};

// Rotate session ID on logout/timeout and reset user identity / user_key
export function resetAnalyticsSession() {
  const newId = generateUUID();
  inMemorySessionId = newId;
  analyticsWebSessionStorage.setItem("availio_session_id", newId);
  try {
    if (POSTHOG_ACTIVE && typeof posthog.reset === "function") {
      posthog.reset();
    }
  } catch (error) {
    console.warn("Failed to reset PostHog session:", error);
  }
  try {
    queryClient.removeQueries({ queryKey: ["userProfile"] });
    queryClient.removeQueries({ queryKey: ["userInfo"] });
    queryClient.removeQueries({ queryKey: ["userDetails"] });
  } catch (error) {
    console.warn(
      "Failed to clear userProfile queries on session reset:",
      error
    );
  }
  analyticsLocalStorage.removeItem("availio_registered_email_hash");
  analyticsLocalStorage.removeItem("availio_registration_submitted_at");
}

// Reset user identity / user_key on signout
export const resetUserKey = (): void => {
  resetAnalyticsSession();
};

// Helper functions for context block
const getLocale = (): "de" | "fr" | "it" | "en" => {
  const lang = (i18n.language || "en").toLowerCase().slice(0, 2);
  if (["de", "fr", "it", "en"].includes(lang)) {
    return lang as "de" | "fr" | "it" | "en";
  }
  return "en";
};

const getPlatform = (): "pwa" | "browser" => {
  if (typeof window === "undefined") return "browser";
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ||
    document.referrer.includes("android-app://");
  return isStandalone ? "pwa" : "browser";
};

const getPointer = (): "touch" | "pointer" => {
  if (typeof window === "undefined") return "pointer";
  const hasTouchScreen =
    ("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0) ||
    ("msMaxTouchPoints" in navigator &&
      (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints >
        0);
  return hasTouchScreen ? "touch" : "pointer";
};

const getChannel = (): "webapp" | "kiosk" => {
  const envMode = import.meta.env.VITE_APP_TARGET;
  return envMode === "kiosk" ? "kiosk" : "webapp";
};

export const getUserId = (
  explicitUserId?: string | number | null
): string | number | null => {
  if (explicitUserId) {
    // Explicitly reject email addresses - email must never be used as userId or distinct_id
    if (typeof explicitUserId === "string" && explicitUserId.includes("@")) {
      return null;
    }
    return explicitUserId;
  }
  try {
    const allProfileQueries = queryClient.getQueriesData<
      userProfile | Record<string, unknown>
    >({
      queryKey: ["userProfile"],
    });
    for (const [, queryData] of allProfileQueries) {
      if (!queryData) continue;
      const candidate =
        (queryData as userProfile)?.profile?.id ||
        (queryData as Record<string, unknown>)?.id ||
        (queryData as Record<string, unknown>)?.userId ||
        (queryData as Record<string, unknown>)?.profileId ||
        (queryData as Record<string, unknown>)?.profileUuid;
      if (
        candidate &&
        (typeof candidate !== "string" || !candidate.includes("@"))
      ) {
        return candidate as string | number;
      }
    }
  } catch {
    // Fall back to null
  }
  return null;
};

export const resolveUserKey = (
  explicitUserId?: string | number | null
): string | null => {
  const userId = getUserId(explicitUserId);
  return userId ? String(userId) : null;
};

export const captureEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
  explicitUserKey?: string | null
) => {
  if (!POSTHOG_ACTIVE) return;

  const userKey =
    explicitUserKey !== undefined ? explicitUserKey : resolveUserKey();

  // Ensure PostHog distinct_id matches user_key for authenticated users.
  // STRICT RULE: NEVER use email, email_hash, or raw PII for distinct_id.
  if (
    userKey &&
    !userKey.includes("@") &&
    typeof posthog.get_distinct_id === "function" &&
    posthog.get_distinct_id() !== userKey
  ) {
    try {
      posthog.identify(userKey);
    } catch (error) {
      console.warn("Failed to align PostHog distinct_id with user_key:", error);
    }
  }

  const contextBlock = {
    app_version: "0.0.0",
    channel: getChannel(),
    context: "availio",
    event_id: generateUUID(),
    event_name: eventName,
    locale: getLocale(),
    platform: getPlatform(),
    pointer: getPointer(),
    session_id: getAnalyticsSessionId(),
    timestamp: new Date().toISOString(),
    user_key: userKey,
  };

  try {
    posthog.capture(eventName, {
      ...contextBlock,
      ...properties,
    });
  } catch (error) {
    console.warn("Failed to capture analytics event:", error);
  }
};

export const trackRegistrationStarted = (props?: { entry_point?: string }) => {
  if (!POSTHOG_ACTIVE) return;

  captureEvent("registration_started", {
    ...(props?.entry_point ? { entry_point: props.entry_point } : {}),
  });
};

export const trackRegistrationSubmitted = (
  email?: string,
  userKey?: string
) => {
  if (!POSTHOG_ACTIVE) return;

  const emailHash = email ? md5(email.trim().toLowerCase()) : undefined;
  if (emailHash) {
    analyticsLocalStorage.setItem("availio_registered_email_hash", emailHash);
    analyticsLocalStorage.setItem(
      "availio_registration_submitted_at",
      new Date().toISOString()
    );
  }
  captureEvent("registration_submitted", {
    ...(emailHash ? { email_hash: emailHash, user_key: userKey } : {}),
  });
};

let inMemoryBookingAttemptId = "";

// Booking Attempt ID management (ANL-BKG-013)
export const getBookingAttemptId = (): string => {
  return (
    analyticsWebSessionStorage.getItem("availio_booking_attempt_id") ||
    inMemoryBookingAttemptId ||
    ""
  );
};

export const mintBookingAttemptId = (): string => {
  const newId = generateUUID();
  inMemoryBookingAttemptId = newId;
  analyticsWebSessionStorage.setItem("availio_booking_attempt_id", newId);
  return newId;
};

export const clearBookingAttemptId = (): void => {
  inMemoryBookingAttemptId = "";
  analyticsWebSessionStorage.removeItem("availio_booking_attempt_id");
  analyticsWebSessionStorage.removeItem("availio_last_step_name");
  analyticsWebSessionStorage.removeItem("availio_last_step_entered_at");
};

// Step-transition tracking with dwell time (ANL-BKG-010)
export const captureStepTransitionEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  if (!POSTHOG_ACTIVE) return;

  const now = new Date();
  const nowISO = now.toISOString();

  const priorStepName =
    analyticsWebSessionStorage.getItem("availio_last_step_name") || undefined;
  let priorStepExit: string | undefined;
  let dwellSeconds: number | undefined;
  const lastEnteredAtStr = analyticsWebSessionStorage.getItem(
    "availio_last_step_entered_at"
  );
  if (lastEnteredAtStr) {
    const lastEnteredAt = parseInt(lastEnteredAtStr, 10);
    if (!isNaN(lastEnteredAt)) {
      priorStepExit = nowISO;
      dwellSeconds = Math.max(
        0,
        Math.round((now.getTime() - lastEnteredAt) / 1000)
      );
    }
  }

  analyticsWebSessionStorage.setItem("availio_last_step_name", eventName);
  analyticsWebSessionStorage.setItem(
    "availio_last_step_entered_at",
    String(now.getTime())
  );

  captureEvent(eventName, {
    prior_step: priorStepName,
    prior_step_dwell_seconds: dwellSeconds,
    prior_step_exit: priorStepExit,
    step_entered_at: nowISO,
    ...properties,
  });
};

export const trackLocationSelected = (properties: {
  booking_attempt_id?: string;
  location_id: string;
  sport?: string;
}) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureStepTransitionEvent("location_selected", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const getSlotsAvailableCount = (
  slots?: BookingFetchResponseType | null
): number => {
  if (!slots || !slots.resources) return 0;
  let count = 0;
  for (const r of slots.resources) {
    if (r.slotInfo?.slots?.availableSlots) {
      count += r.slotInfo.slots.availableSlots.length;
    }
  }
  return count;
};

/**
 * Calculates lead time in whole hours from now until slot start.
 * Supports either (date, time) or a single ISO/datetime string (slot_start).
 */
export const getLeadTimeHours = (
  dateOrSlotStart?: string,
  time?: string
): number => {
  if (!dateOrSlotStart) return 0;
  const slotMoment = time
    ? moment(`${dateOrSlotStart} ${time}`, "YYYY-MM-DD HH:mm")
    : moment(dateOrSlotStart);

  return slotMoment.isValid()
    ? Math.max(0, Math.round(slotMoment.diff(moment(), "hours", true)))
    : 0;
};

/**
 * Extracts localized sport name from location resource terminology.
 */
export const getSport = (
  location?: Partial<locationType> | null
): string | undefined => {
  const singular = location?.resourceType?.terminology?.singular;
  return singular ? applyLocalization(singular) : undefined;
};

/**
 * Resolves total participant count:
 * - If guest payload has guests: payload.guest.length + 1 (organizer)
 * - Else if guestList has guests: guestList.length + 1
 * - Else if bookingParticipants has participants: bookingParticipants.length
 * - Else if editParticipants has participants: editParticipants.length
 * - Defaults to 1 (organizer).
 */
export const getParticipantCount = (
  options: ParticipantCountOptions = {}
): number => {
  if (options.guestPayload?.length) {
    return options.guestPayload.length;
  }
  if (options.guestList?.length) {
    return options.guestList.length;
  }
  if (options.bookingParticipants?.length) {
    return options.bookingParticipants.length;
  }
  if (options.editParticipants?.length) {
    return options.editParticipants.length;
  }
  return 0;
};

/**
 * Resolves previous price from booking edit data.
 */
export const getPreviousPrice = (
  bookingEditData?: {
    totalPrice?: number | null;
    basePrice?: number | null;
  } | null
): number | undefined => {
  if (typeof bookingEditData?.totalPrice === "number") {
    return bookingEditData.totalPrice;
  }
  if (typeof bookingEditData?.basePrice === "number") {
    return bookingEditData.basePrice;
  }
  return undefined;
};

/**
 * Calculates price difference rounded to 2 decimal places.
 */
export const getPriceDifference = (
  currentPrice?: number | string,
  previousPrice?: number | string
): number | undefined => {
  if (currentPrice === undefined || previousPrice === undefined) {
    return undefined;
  }
  const curr = Number(currentPrice);
  const prev = Number(previousPrice);
  if (Number.isNaN(curr) || Number.isNaN(prev)) {
    return undefined;
  }
  return Math.round((curr - prev) * 100) / 100;
};

export const trackAvailabilityViewed = (
  properties: TrackAvailabilityViewedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureStepTransitionEvent("availability_viewed", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const trackSlotSelected = (properties: TrackSlotSelectedProperties) => {
  captureStepTransitionEvent("slot_selected", {
    ...properties,
    slot_duration: Number(properties.slot_duration) || "",
  });
};

export const trackBookingDetailsViewed = (
  properties: TrackBookingDetailsViewedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureStepTransitionEvent("booking_details_viewed", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const trackPaymentStepReached = (
  properties: TrackPaymentStepReachedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureStepTransitionEvent("payment_step_reached", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const trackLocationListViewed = (
  properties: TrackLocationListViewedProperties
) => {
  captureEvent("location_list_viewed", { ...properties });
};

export const trackBookingConfirmed = (
  properties: TrackBookingConfirmedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureEvent("booking_confirmed", {
    booking_category: "single",
    created_by: "user",
    ...properties,
    booking_attempt_id: attemptId || undefined,
    participant_count: Number(properties.participant_count) || 0,
    price:
      typeof properties.price === "number"
        ? properties.price
        : Number(properties.price) || 0,
    slot_duration: Number(properties.slot_duration) || 0,
  });
  clearBookingAttemptId();
};

export const trackBookingEdited = (
  properties: TrackBookingEditedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();

  const priceDifference =
    properties.price_difference ??
    getPriceDifference(properties.price, properties.previous_price);

  const leadTimeHours =
    properties.booking_lead_time_hours ??
    (properties.slot_start ? getLeadTimeHours(properties.slot_start) : 0);

  captureEvent("booking_edited", {
    booking_category: "single",
    edited_by: "user",
    ...properties,
    booking_attempt_id: attemptId || undefined,
    booking_lead_time_hours: leadTimeHours ?? 0,
    participant_count: Number(properties.participant_count) || 0,
    price:
      typeof properties.price === "number"
        ? properties.price
        : Number(properties.price) || 0,
    price_difference: priceDifference,
    slot_duration: Number(properties.slot_duration) || 0,
  });
};

export const trackBookingEditStart = (
  properties: TrackBookingEditStartProperties
) => {
  if (!POSTHOG_ACTIVE) return;

  const leadTimeHours =
    properties.booking_lead_time_hours ??
    (properties.slot_start ? getLeadTimeHours(properties.slot_start) : 0);

  captureEvent("booking_edit_start", {
    booking_category: "single",
    entry_point: "direct_url",
    ...properties,
    booking_lead_time_hours: leadTimeHours ?? 0,
    participant_count: Number(properties.participant_count) || 0,
    price:
      typeof properties.price === "number"
        ? properties.price
        : Number(properties.price) || 0,
    slot_duration: Number(properties.slot_duration) || 0,
  });
};

export const trackBookingEditSubmitted = (
  properties: TrackBookingEditSubmittedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();

  const priceDifference =
    properties.price_difference ??
    getPriceDifference(properties.price, properties.previous_price);

  const leadTimeHours =
    properties.booking_lead_time_hours ??
    (properties.slot_start ? getLeadTimeHours(properties.slot_start) : 0);

  captureEvent("booking_edit_submitted", {
    booking_category: "single",
    edited_by: "user",
    ...properties,
    booking_attempt_id: attemptId || undefined,
    booking_lead_time_hours: leadTimeHours ?? 0,
    participant_count: Number(properties.participant_count) || 0,
    price:
      typeof properties.price === "number"
        ? properties.price
        : Number(properties.price) || 0,
    price_difference: priceDifference,
    slot_duration: Number(properties.slot_duration) || 0,
  });
};

export const resolveCancellationMetrics = (
  responseData?: unknown,
  cancelFeasibility?: unknown
): CancellationMetricsResult => {
  const resDataObj = (responseData as { data?: Record<string, unknown> })?.data;
  const feasObj = cancelFeasibility as Record<string, unknown> | undefined;

  const hoursBefore =
    resDataObj?.hours_before_slot_start ??
    feasObj?.hours_before_slot_start ??
    0;

  const relativeToDeadline =
    resDataObj?.relative_to_deadline ??
    feasObj?.relative_to_deadline ??
    (Number(hoursBefore) >= 24 ? "before_24h" : "after_24h");

  return {
    hours_before_slot_start: Number(hoursBefore),
    relative_to_deadline: String(relativeToDeadline),
  };
};

export const trackBookingCancelled = (
  propertiesOrBookingId: string | number | TrackBookingCancelledProperties,
  responseData?: unknown,
  cancelFeasibility?: unknown,
  cancelledBy: "user" | "admin" | string = "user"
) => {
  let bookingId: string | number = "";
  let resolvedCancelledBy: string = "user";
  let rawResponseData: unknown = responseData;
  let rawCancelFeasibility: unknown = cancelFeasibility;
  let directHoursBefore: number | undefined = undefined;
  let directRelativeToDeadline: string | undefined = undefined;

  if (
    typeof propertiesOrBookingId === "string" ||
    typeof propertiesOrBookingId === "number"
  ) {
    bookingId = propertiesOrBookingId;
    resolvedCancelledBy = cancelledBy;
  } else if (
    typeof propertiesOrBookingId === "object" &&
    propertiesOrBookingId !== null
  ) {
    bookingId =
      propertiesOrBookingId.booking_id ?? propertiesOrBookingId.bookingId ?? "";
    resolvedCancelledBy =
      propertiesOrBookingId.cancelled_by ??
      propertiesOrBookingId.cancelledBy ??
      cancelledBy;
    rawResponseData = propertiesOrBookingId.responseData ?? responseData;
    rawCancelFeasibility =
      propertiesOrBookingId.cancelFeasibility ?? cancelFeasibility;
    directHoursBefore = propertiesOrBookingId.hours_before_slot_start;
    directRelativeToDeadline = propertiesOrBookingId.relative_to_deadline;
  }

  const calculated = resolveCancellationMetrics(
    rawResponseData,
    rawCancelFeasibility
  );

  const hours_before_slot_start =
    directHoursBefore !== undefined
      ? directHoursBefore
      : calculated.hours_before_slot_start;

  const relative_to_deadline =
    directRelativeToDeadline !== undefined
      ? directRelativeToDeadline
      : calculated.relative_to_deadline;

  captureEvent("booking_cancelled", {
    booking_id: bookingId,
    cancelled_by: resolvedCancelledBy,
    hours_before_slot_start: Number(hours_before_slot_start),
    relative_to_deadline: String(relative_to_deadline),
  });
};

export const trackLoginSucceeded = async (
  options?: TrackLoginSucceededOptions
) => {
  if (!POSTHOG_ACTIVE) return;

  let resolvedKey: string | null = null;
  let resolvedEmail: string | undefined = options?.email || undefined;

  if (options?.userId) {
    resolvedKey = resolveUserKey(options.userId);
  } else if (options?.userKey && !options.userKey.includes("@")) {
    resolvedKey = options.userKey;
  }

  // Directly fetch user profile to retrieve internal user ID
  if (!resolvedKey && typeof getUserProfile === "function") {
    try {
      const profileData = await getUserProfile();
      if (profileData?.ok && profileData?.data) {
        const id = profileData.data?.profile?.id;
        if (id && (typeof id !== "string" || !id.includes("@"))) {
          resolvedKey = String(id);
        }
        if (profileData.data?.email && !resolvedEmail) {
          resolvedEmail = profileData.data.email;
        }
        try {
          queryClient.setQueryData(
            ["userProfile", id || "active"],
            profileData.data
          );
        } catch {
          // Non-blocking queryClient update
        }
      }
    } catch {
      // Non-blocking fallback
    }
  }

  if (!resolvedKey) {
    resolvedKey = resolveUserKey();
  }

  if (!resolvedEmail) {
    const allProfileQueries = queryClient.getQueriesData<userProfile>({
      queryKey: ["userProfile"],
    });
    for (const [, queryData] of allProfileQueries) {
      if (queryData?.email) {
        resolvedEmail = queryData.email;
        break;
      }
    }
  }

  const emailHash = resolvedEmail
    ? md5(resolvedEmail.trim().toLowerCase())
    : undefined;

  // Capture login_succeeded with user_key in context block and email_hash in event properties
  captureEvent(
    "login_succeeded",
    emailHash ? { email_hash: emailHash } : undefined,
    resolvedKey
  );

  // Set user_key as distinct_id in PostHog immediately after login_succeeded.
  // STRICT RULE: NEVER use email, email_hash, or raw PII for distinct_id.
  // PostHog distinct_id must ONLY be user_key (MD5 hash of internal user ID).
  if (
    resolvedKey &&
    !resolvedKey.includes("@") &&
    resolvedKey !== emailHash &&
    resolvedKey !== resolvedEmail &&
    POSTHOG_ACTIVE
  ) {
    try {
      posthog.identify(resolvedKey);
    } catch (error) {
      console.warn("Failed to identify user in PostHog:", error);
    }
  }
};

export const trackUserLoggedOut = (userKey?: string | null) => {
  if (!POSTHOG_ACTIVE) return;
  const resolvedKey = userKey || resolveUserKey();
  captureEvent(
    "user_logged_out",
    resolvedKey ? { user_key: resolvedKey } : undefined,
    resolvedKey
  );
};

export const trackChargeSucceeded = (
  properties: TrackChargeSucceededProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureEvent("charge_succeeded", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const trackChargeFailed = (properties: TrackChargeFailedProperties) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureEvent("charge_failed", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
  });
};

export const trackChargeRefunded = (
  properties: TrackChargeRefundedProperties
) => {
  const attemptId = properties.booking_attempt_id || getBookingAttemptId();
  captureEvent("charge_refunded", {
    ...properties,
    booking_attempt_id: attemptId || undefined,
    created_by: "user",
  });
};

const resolveRaiseNowPaymentData = (
  payload?: Record<string, unknown> | null,
  bookingId?: string | number
) => {
  const data = payload || {};
  const paymentData =
    (data.paymentData as Record<string, unknown> | undefined) ||
    (data.brand || data.type ? data : null);

  const brand = String(
    paymentData?.brand ||
      paymentData?.type ||
      paymentData?.paymentMethod ||
      data.payment_method ||
      data.paymentMethod ||
      ""
  );
  const brandLower = brand.toLowerCase();
  const typeLower = String(paymentData?.type || data.type || "").toLowerCase();
  const isTwint = brandLower.includes("twint") || typeLower.includes("twint");
  const payment_method = isTwint ? "TWINT" : brand || "Card";

  const resolvedBookingId =
    (data.booking_id as string | number | undefined) ||
    (data.bookingId as string | number | undefined) ||
    ((data.data as Record<string, unknown>)?.booking_id as
      | string
      | number
      | undefined) ||
    ((data.data as Record<string, unknown>)?.bookingId as
      | string
      | number
      | undefined) ||
    bookingId ||
    "";

  return { data, payment_method, resolvedBookingId };
};

export const trackRaiseNowChargeSucceeded = (
  payload?: Record<string, unknown> | null,
  bookingId?: string | number,
  fallbackAmount?: number | string,
  isEdit?: boolean
): TrackRaiseNowChargeResult | undefined => {
  if (!POSTHOG_ACTIVE) return;
  const { data, payment_method, resolvedBookingId } =
    resolveRaiseNowPaymentData(payload, bookingId);

  const resolvedAmount =
    typeof data.amount === "number"
      ? data.amount
      : typeof (data.data as Record<string, unknown>)?.amount === "number"
        ? Number((data.data as Record<string, unknown>).amount)
        : typeof fallbackAmount === "number"
          ? fallbackAmount
          : parseFloat(String(fallbackAmount || 0)) || 0;

  trackChargeSucceeded({
    action: isEdit ? "edit" : "create",
    amount: resolvedAmount,
    booking_id: resolvedBookingId,
    payment_method,
    pre_auth: true,
  });

  return {
    amount: resolvedAmount,
    booking_id: resolvedBookingId,
    payment_method,
  };
};

export const trackRaiseNowChargeFailed = (
  payload?: Record<string, unknown> | null,
  bookingId?: string | number,
  isEdit?: boolean
) => {
  if (!POSTHOG_ACTIVE) return;
  const { data, payment_method, resolvedBookingId } =
    resolveRaiseNowPaymentData(payload, bookingId);

  const failureReason =
    data.failure_reason ||
    data.failureReason ||
    data.reason ||
    data.errorMessage ||
    data.error ||
    data.message ||
    (data.data as Record<string, unknown>)?.failure_reason ||
    (data.data as Record<string, unknown>)?.failureReason ||
    (data.data as Record<string, unknown>)?.error ||
    (data.status ? String(data.status).toLowerCase() : "failed");

  trackChargeFailed({
    action: isEdit ? "edit" : "create",
    booking_id: resolvedBookingId,
    failure_reason: String(failureReason),
    payment_method,
    pre_auth: false,
  });
};

export const trackResponseError = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  res: any,
  explicitMessage?: string
) => {
  if (!POSTHOG_ACTIVE) return;

  const status = typeof res?.status === "number" ? res.status : null;
  const eventName = status ? `error_${status}` : "error_unknown";

  let message = explicitMessage;
  if (!message) {
    if (typeof res?.data === "string" && res.data.trim()) {
      message = res.data;
    } else if (res?.data?.message) {
      message = String(res.data.message);
    } else if (res?.data?.error) {
      message = String(res.data.error);
    } else if (res?.problem) {
      message = String(res.problem);
    } else if (res?.originalError?.message) {
      message = String(res.originalError.message);
    } else {
      message = "Something went wrong";
    }
  }

  const endpoint = res?.config?.url || undefined;
  const method = res?.config?.method
    ? String(res.config.method).toUpperCase()
    : undefined;
  const problem = res?.problem ? String(res.problem) : undefined;

  captureEvent(eventName, {
    endpoint,
    message: String(message || "Something went wrong"),
    method,
    problem,
    status,
  });
};

export const trackApiError = trackResponseError;
